// The sign-in drawing (GIOTHUB-185; the layout of the Floor Plan Studio
// login approved by Massimo on 2026-10-25, also used by the device store):
// a dimensioned device board as a dark blueprint, drawn into an <svg>.
// Illustrative only - a sensor main board with the parts a device type
// describes, one of them selected as in the builder's key detail.
const NS = "http://www.w3.org/2000/svg";

type Attrs = Record<string, string | number>;

export function drawBlueprint(svg: SVGSVGElement) {
  const el = (tag: string, attrs: Attrs, parent: Element = svg) => {
    const e = document.createElementNS(NS, tag) as SVGGraphicsElement;
    for (const k in attrs) e.setAttribute(k, String(attrs[k]));
    parent.appendChild(e);
    return e;
  };
  const S = 6; // px per mm
  const BX = 150, BY = 120; // board origin
  const X = (v: number) => BX + v * S;
  const Y = (v: number) => BY + v * S;
  const W = 100, H = 56; // board, mm

  // grid: 1 mm and 5 mm, beyond the viewBox (the picture may be wider)
  const grid = el("g", {});
  for (let gx = -40; gx <= 160; gx++)
    el("line", { x1: X(gx), y1: -1200, x2: X(gx), y2: 1800, stroke: gx % 5 ? "var(--bp-grid)" : "var(--bp-grid5)", "stroke-width": 1 }, grid);
  for (let gy = -40; gy <= 110; gy++)
    el("line", { x1: -1200, y1: Y(gy), x2: 2400, y2: Y(gy), stroke: gy % 5 ? "var(--bp-grid)" : "var(--bp-grid5)", "stroke-width": 1 }, grid);

  const label = (x: number, y: number, t: string, size = 11, anchor = "middle", fill = "var(--bp-ink)", ls = ".06em") => {
    const e = el("text", { x, y, "text-anchor": anchor, "font-family": "IBM Plex Mono, monospace", "font-size": size, fill, "letter-spacing": ls });
    e.textContent = t;
    return e;
  };
  const outline = el("g", { fill: "none", stroke: "var(--bp-wall)", "stroke-linejoin": "round" });
  const stroke = (d: string, w = 2.5) => {
    const p = el("path", { d, "stroke-width": w, class: "draw" }, outline) as SVGPathElement;
    p.style.setProperty("--len", String(p.getTotalLength()));
    return p;
  };
  const rr = (x: number, y: number, w: number, h: number, r: number) =>
    `M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + h - r}A${r} ${r} 0 0 1 ${x + w - r} ${y + h}` +
    `H${x + r}A${r} ${r} 0 0 1 ${x} ${y + h - r}V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`;
  const ink = el("g", { fill: "rgba(159, 212, 232, .06)", stroke: "var(--bp-ink)", "stroke-width": 1.2 });
  const part = (x: number, y: number, w: number, h: number, ref: string, name = "") => {
    el("rect", { x: X(x), y: Y(y), width: w * S, height: h * S, rx: 2 }, ink);
    label(X(x + w / 2), Y(y + h / 2) + (name ? -2 : 4), ref, 10);
    if (name) label(X(x + w / 2), Y(y + h / 2) + 11, name, 8.5, "middle", "var(--bp-quiet)");
  };

  // board outline, mounting holes
  stroke(rr(X(0), Y(0), W * S, H * S, 3 * S));
  const holes: [number, number][] = [[4, 4], [W - 4, 4], [4, H - 4], [W - 4, H - 4]];
  for (const [hx, hy] of holes) {
    el("circle", { cx: X(hx), cy: Y(hy), r: 1.6 * S, fill: "none", stroke: "var(--bp-wall)", "stroke-width": 1.4 });
    el("path", { d: `M${X(hx) - 5} ${Y(hy)}h10M${X(hx)} ${Y(hy) - 5}v10`, stroke: "var(--bp-quiet)", "stroke-width": 1 });
  }

  // traces (behind the parts)
  const tr = el("g", { fill: "none", stroke: "var(--bp-quiet)", "stroke-width": 1.4, "stroke-linejoin": "round", opacity: 0.8 });
  const trace = (pts: [number, number][]) => el("path", { d: "M" + pts.map(([x, y]) => `${X(x)} ${Y(y)}`).join("L") }, tr);
  trace([[36, 27], [44, 27], [44, 22], [52, 22]]);
  trace([[36, 29], [46, 29], [46, 25], [52, 25]]);
  trace([[69.7, 18], [80, 18], [86, 9]]);
  trace([[30, 33], [30, 40], [40, 40]]);
  trace([[30, 33], [26, 37], [26, 45], [11, 45]]);
  trace([[24, 27], [16, 27], [16, 15], [11, 15]]);
  trace([[33, 33], [33, 46], [60, 46], [60, 42]]);

  // parts
  part(24, 21, 12, 12, "U1", "nRF54L15");
  part(52, 10, 17.7, 15.8, "U2", "LTE EG800AK");
  part(56, 34, 9, 8, "U3", "flash");
  part(70, 34, 7, 5, "U5", "gauge");
  part(4, 11, 7, 8, "BT1", "3V6");
  part(4, 41, 7, 9, "J1", "sensor");
  part(14, 7, 5, 3.2, "Y1");
  part(44, 47, 3, 2, "D1");
  // pins of U1
  const pins = el("g", { stroke: "var(--bp-ink)", "stroke-width": 1 });
  for (let i = 1; i < 12; i += 1.5) {
    el("path", { d: `M${X(24 + i)} ${Y(21)}v-5M${X(24 + i)} ${Y(33)}v5M${X(24)} ${Y(21 + i)}h-5M${X(36)} ${Y(21 + i)}h5` }, pins);
  }
  // antenna with its keep-out
  const ko = el("g", {});
  el("rect", { x: X(80), y: Y(1.5), width: 18 * S, height: 10 * S, fill: "none", stroke: "var(--bp-quiet)", "stroke-width": 1, "stroke-dasharray": "4 3" }, ko);
  for (let i = 0; i < 9; i++)
    el("path", { d: `M${X(80 + i * 2)} ${Y(11.5)}l${10 * S * 0.6} ${-10 * S * 0.6}`, stroke: "var(--bp-grid5)", "stroke-width": 1 }, ko);
  part(84, 5, 10, 3, "ANT1");
  label(X(89), Y(14.5), "KEEP-OUT", 8.5, "middle", "var(--bp-quiet)");

  // the selected part, as in the key detail: U4 accelerometer -> its catalogue key
  const [sx, sy, sw, sh] = [40, 38.5, 3, 3];
  part(sx, sy, sw, sh, "");
  label(X(sx + sw / 2), Y(sy + sh + 3.2), "U4", 9);
  const sel = el("g", {});
  el("rect", { x: X(sx) - 5, y: Y(sy) - 5, width: sw * S + 10, height: sh * S + 10, fill: "none", stroke: "var(--bp-pick)", "stroke-width": 1.4, "stroke-dasharray": "5 3" }, sel);
  for (const [hx, hy] of [[0, 0], [1, 0], [0, 1], [1, 1]])
    el("rect", { x: X(sx) - 8 + hx * (sw * S + 10), y: Y(sy) - 8 + hy * (sh * S + 10), width: 6, height: 6, fill: "var(--bp-pick)" }, sel);
  el("path", { d: `M${X(sx + sw) + 6} ${Y(sy + sh) + 6}L${X(sx + 22)} ${Y(50)}H${X(sx + 25)}`, fill: "none", stroke: "var(--bp-pick)", "stroke-width": 1 }, sel);
  sel.appendChild(label(X(sx + 26), Y(49.3), "U4 · LIS2DTW12 accelerometer", 10, "start", "var(--bp-pick)", ".02em"));
  sel.appendChild(label(X(sx + 26), Y(51.8), "key tilt_deg · role angle · [deg]", 10, "start", "var(--bp-pick)", ".02em"));

  // dimension lines
  const dim = el("g", { stroke: "var(--bp-quiet)", "stroke-width": 1 });
  el("line", { x1: X(0), y1: Y(-6), x2: X(W), y2: Y(-6) }, dim);
  for (const x of [0, W]) el("line", { x1: X(x), y1: Y(-8), x2: X(x), y2: Y(-1) }, dim);
  label(X(W / 2), Y(-7), "100.0", 11);
  el("line", { x1: X(-6), y1: Y(0), x2: X(-6), y2: Y(H) }, dim);
  for (const y of [0, H]) el("line", { x1: X(-8), y1: Y(y), x2: X(-1), y2: Y(y) }, dim);
  const v = label(X(-7), Y(H / 2), "56.0", 11);
  v.setAttribute("transform", `rotate(-90 ${X(-7)} ${Y(H / 2)})`);
  el("line", { x1: X(4), y1: Y(H + 6), x2: X(W - 4), y2: Y(H + 6) }, dim);
  for (const x of [4, W - 4]) el("line", { x1: X(x), y1: Y(H - 2), x2: X(x), y2: Y(H + 8) }, dim);
  label(X(W / 2), Y(H + 5), "92.0", 11);
  el("path", { d: `M${X(W - 4) + 7} ${Y(H - 4) + 7}L${X(W + 6)} ${Y(H + 2)}H${X(W + 15)}`, fill: "none" }, dim);
  label(X(W + 6) + 4, Y(H + 1.2), "4× Ø3.2", 10, "start");

  // scale bar
  const sb = el("g", {});
  for (let i = 0; i < 2; i++)
    el("rect", { x: X(W + 6 + i * 5), y: Y(H + 12), width: 5 * S, height: 6, fill: i ? "none" : "var(--bp-ink)", stroke: "var(--bp-ink)", "stroke-width": 1 }, sb);
  label(X(W + 6), Y(H + 15), "0", 9, "middle", "var(--bp-quiet)");
  label(X(W + 11), Y(H + 15), "5", 9, "middle", "var(--bp-quiet)");
  label(X(W + 16), Y(H + 15), "10 mm", 9, "middle", "var(--bp-quiet)");
}
